import React from 'react';
import {
  ST_AUTOPILOT_HIGH_IMPACT,
  ST_AUTOPILOT_LOW_RISK,
} from '../../growth-settings/mockData';
import type { StToggleItem } from '../../growth-settings/types';
import { StDisplayHeading, StSectionCard, StToggleRow, type StSectionSaveFooter } from './SettingsPrimitives';

const LOW_RISK_LABELS = new Set(ST_AUTOPILOT_LOW_RISK.map((rule) => rule.label));
const HIGH_IMPACT_LABELS = new Set(ST_AUTOPILOT_HIGH_IMPACT.map((rule) => rule.label));

function splitAutopilotToggles(toggles: StToggleItem[]) {
  const lowRisk: StToggleItem[] = [];
  const highImpact: StToggleItem[] = [];
  for (const rule of toggles) {
    if (LOW_RISK_LABELS.has(rule.label)) lowRisk.push(rule);
    else if (HIGH_IMPACT_LABELS.has(rule.label)) highImpact.push(rule);
  }
  return {
    lowRisk: lowRisk.length ? lowRisk : ST_AUTOPILOT_LOW_RISK,
    highImpact: highImpact.length ? highImpact : ST_AUTOPILOT_HIGH_IMPACT,
  };
}

export interface SettingsAutopilotProps {
  toggles?: StToggleItem[];
  onToggleChange?: (key: string, enabled: boolean) => void;
  saveFooter?: StSectionSaveFooter;
}

export function SettingsAutopilot({
  toggles = [...ST_AUTOPILOT_LOW_RISK, ...ST_AUTOPILOT_HIGH_IMPACT],
  onToggleChange,
  saveFooter,
}: SettingsAutopilotProps) {
  const { lowRisk, highImpact } = splitAutopilotToggles(toggles);

  return (
    <StSectionCard
      title="Autopilot Rules"
      description="Control what ELVA can do automatically vs. what requires your approval."
      {...(saveFooter ? { saveFooter } : {})}
    >
      <section
        className="rounded-[var(--r-md)] border px-4 py-4"
        style={{
          borderColor: 'color-mix(in srgb, var(--success) 35%, transparent)',
          background: 'var(--success-tint)',
        }}
      >
        <p className="text-[13px] leading-[1.6] text-[var(--success)]">
          Turning on autopilot for an action means ELVA will do it automatically and log the change.
          You can always review changes in the campaign log. Turning it off means ELVA will suggest
          the action but wait for your approval.
        </p>
      </section>

      <section>
        <StDisplayHeading
          title="Low-Risk Actions"
          sub="These are routine optimizations that happen daily. Recommended: keep on autopilot."
        />
        <div className="mt-3">
          {lowRisk.map((rule) => (
            <StToggleRow
              key={rule.key ?? rule.label}
              label={rule.label}
              description={rule.description}
              enabled={rule.enabled}
              {...(rule.key && onToggleChange
                ? { onEnabledChange: (next: boolean) => onToggleChange(rule.key!, next) }
                : {})}
            />
          ))}
        </div>
      </section>

      <section>
        <StDisplayHeading
          title="Higher-Impact Actions"
          sub="These change your campaign structure or spending. Recommended: require approval until you're comfortable."
        />
        <div className="mt-3">
          {highImpact.map((rule) => (
            <StToggleRow
              key={rule.key ?? rule.label}
              label={rule.label}
              description={rule.description}
              enabled={rule.enabled}
              {...(rule.key && onToggleChange
                ? { onEnabledChange: (next: boolean) => onToggleChange(rule.key!, next) }
                : {})}
            />
          ))}
        </div>
      </section>
    </StSectionCard>
  );
}
